import React, { useEffect, useState } from "react";
import { routes } from "../../../../../constants";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { CameraIcon, DropDownCloseIcon } from "../../../../../assets/svg";
import {
  disableOnWheel,
  sanitizeDataTypeClientSide,
  validatorFunctionToAllowOnlyEnglish,
} from "../../../../../utils/utils";
import { Form, Input, Typography, Button, Select, Flex, Row, Col } from "antd";
import {
  fileImageUpload,
  getCityAction,
  getCountryAction,
  getStateAction,
  setupProfile,
} from "../../../../../redux/services/auth/authServices";
import { baseRouteMaker } from "../../../../../constants/routeConsts";
import {
  cleanupCityList,
  cleanupStateList,
} from "../../../../../redux/reducers/authReducer";
import RequiredLabel from "../../../../common/required-label/RequiredLabel";
import logo from "../../../../../assets/images/logo.png";
import LanguageDropdown from "../../../../common/language-dropdown/LanguageDropdown";
import { FileAttachment } from "../../../../common/fileAttachment";
import { useRef } from "react";
import { colors } from "../../../../../constants/colors";
import useLocalStorage from "../../../../../hooks/useLocalStorage";
import { appConfig } from "../../../../../constants/config/appConfig";
import { CameraOutlined } from "@ant-design/icons";
import styles from "./ProfileSetup.module.scss";
import Actions from "../../../../../redux/actions";
import { allowAlphaNumericCharacters, allowCharactersOnlyForDecimalNumbers } from "../../../../../utils/appKeyStrokeLimitingUtils";

const { Title } = Typography;

const ProfileSetup = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { countryList, stateList, cityList } = useSelector(
    (state) => state?.auth
  );

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  const [imageLoader, setImageLoader] = useState(false);
  /* loading dependencies */

  const [isCountrySelected, setIsCountrySelected] = useState(false);
  const [isStateSelected, setIsStateSelected] = useState(false);
  const [selectedCountryObj, setSelectedCountryObj] = useState(null);
  const [selectedStateObj, setSelectedStateObj] = useState(null);
  const [selectedCityObj, setSelectedCityObj] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const { profileSetUpDetails } = useSelector((state) => state?.auth);

  /* module dependencies for attachment - single */
  const [singleAttachmentObj, setSingleAttachmentObj] = useState(null);
  const [
    singleAttachmentSelectedImageUrl,
    setSingleAttachmentSelectedImageUrl,
  ] = useState("");
  const attachmentRef = useRef(null);
  /* module dependencies for attachment - single */

  /* local storage dependencies */
  const { useSession, setUserSession } = useLocalStorage(
    appConfig.SESSION_USER_INFO
  );
  /* local storage dependencies */

  /* on Image changed- single attachment */
  const onImageChanged = (e) => {
    const file = e.target.files[0];
    setSingleAttachmentObj(file);
    if (file) {
      const formData = new FormData();
      formData.append("image", file);
      let formDataBody = formData;
      setImageLoader(true);
      dispatch(fileImageUpload(formDataBody)).then((apiRes) => {
        const imageUrl = apiRes?.[0];
        setSingleAttachmentSelectedImageUrl(imageUrl);
        setImageLoader(false);
      });
    }
  };
  /* on Image changed- single attachment */

  useEffect(() => {
    if (
      profileSetUpDetails &&
      Object.keys(profileSetUpDetails)?.length !== 0 &&
      profileSetUpDetails !== undefined &&
      profileSetUpDetails !== null
    ) {
      if (profileSetUpDetails?.address?.country_numeric_code) {
        let stateObject = {
          country_code: Number(
            profileSetUpDetails.address?.country_numeric_code
          ),
        };
        dispatch(getStateAction(stateObject));
      }

      if (profileSetUpDetails?.address?.state?.id) {
        let cityBody = {
          stateCode: profileSetUpDetails?.address?.state?.id,
        };
        dispatch(getCityAction(cityBody));
      }
      setIsCountrySelected(true);
      setIsStateSelected(true);

      form.setFieldsValue({
        name: profileSetUpDetails.name,
        designation: profileSetUpDetails.designation,
        branch: profileSetUpDetails.branch,
        director_name: profileSetUpDetails.director_name,
        email: profileSetUpDetails?.email,
        address_line_1: profileSetUpDetails?.address?.address_line_1,
        address_line_2: profileSetUpDetails?.address?.address_line_2,
        country: profileSetUpDetails?.address?.country,
        zip_code: profileSetUpDetails?.address?.zip_code,
        state: profileSetUpDetails?.address?.state?.name,
        city: profileSetUpDetails?.address?.city?.id,
        payment_type: profileSetUpDetails?.payment_type,
        latitude: profileSetUpDetails?.coordinates?.latitude,
        longitude: profileSetUpDetails?.coordinates?.longitude,
      });
      setSelectedStateObj({
        ...profileSetUpDetails?.address?.state,
        id: profileSetUpDetails?.address?.state?.id,
        _id: profileSetUpDetails?.address?.state?._id,
      });
      setSelectedCityObj({
        ...profileSetUpDetails?.address?.city,
        id: profileSetUpDetails?.address?.city?.id,
      });
      setSelectedCountryObj({
        _id: profileSetUpDetails?.address?.country,
        name: profileSetUpDetails?.address?.country_name,
        numeric_code: profileSetUpDetails?.address?.country_numeric_code,
      });
      setSingleAttachmentSelectedImageUrl(profileSetUpDetails?.logo_image);
      setSelectedCurrency(profileSetUpDetails?.currency);
    }
  }, []);

  // country api call
  useEffect(() => {
    dispatch(getCountryAction());
  }, [dispatch]);

  const handleCountryChange = (value, element) => {
    if (value) {
      const selectedCountryObject = countryList?.find(
        (item) => item._id === value
      );
      setSelectedCountryObj(selectedCountryObject);
      if (selectedCountryObject) {
        let stateObject = {
          country_code: selectedCountryObject?.numeric_code,
        };
        dispatch(getStateAction(stateObject));
        dispatch(cleanupStateList());
        dispatch(cleanupCityList());
        setIsCountrySelected(true);
      }
      form.resetFields(["state", "city"]);
    } else {
      dispatch(cleanupStateList());
      dispatch(cleanupCityList());
      setIsCountrySelected(false);
      form.resetFields(["state", "city"]);
    }
  };

  const handleStateChange = (value, element) => {
    const selectedStateObject = stateList?.find((item) => item.id === value);
    setSelectedStateObj(selectedStateObject);
    if (selectedStateObject) {
      let dataToBeSent = {
        stateCode: selectedStateObject?.id,
      };
      dispatch(getCityAction(dataToBeSent));
      dispatch(cleanupCityList());
      setIsStateSelected(true);
      form.resetFields(["city"]);
    }
    else {
      setIsStateSelected(false);
      form.resetFields(["city"]);
    }
  };

  const handleCityChange = (value, element) => {
    const selectedCityObject = cityList?.find(
      (item) => item.id === element?.value
    );
    setSelectedCityObj(selectedCityObject);
  };

  /* on submit */
  const onSubmit = (values) => {
    let dataToBeSent = {
      name: values?.name,
      branch: values?.branch,
      currency: selectedCurrency,
      address: {
        address_line_1: values?.address_line_1,
        address_line_2: values?.address_line_2,
        state_id: selectedStateObj?._id,
        state: {
          _id: selectedStateObj?._id,
          id: selectedStateObj?.id,
          name: selectedStateObj?.name,
          state_code: selectedStateObj?.state_code,
        },
        city: {
          id: selectedCityObj?.id,
          name: selectedCityObj?.name,
        },
        zip_code: values?.zip_code,
        country: selectedCountryObj?._id,
        country_numeric_code: selectedCountryObj?.numeric_code,
        country_name: selectedCountryObj?.name,
      },
      logo_image: singleAttachmentSelectedImageUrl ?? "",
      payment_type: values?.payment_type,
      designation: values?.designation,
      director_name: values?.director_name,
      coordinates: {
        latitude: values?.latitude,
        longitude: values?.longitude,
      },
    };

    if (values?.payment_type === "bank") {
      dispatch(
        Actions.createAction(Actions.PROVIDER_SET_UP_PROFILE, dataToBeSent)
      );
      navigate(baseRouteMaker(routes.ADD_BANK_ACCOUNT));
    } else {
      delete dataToBeSent.address.state_id;
      delete dataToBeSent.address.state._id;
      dispatch(
        setupProfile(dataToBeSent, navigate, setLoading, setUserSession)
      );
    }
  };

  const paymentData = [
    {
      name: "Bank",
      value: "bank",
    },
    {
      name: "Cash",
      value: "cash",
    },
  ];

  return (
    <>
      <Flex vertical gap="2rem" className={styles.profileSetupWrapper}>
        <div className={styles.logoContainer}>
          <Row gutter={16} align="middle">
            <Col span={8}></Col>
            <Col span={8}>
              <img src={logo} alt="ReynaCare" />
            </Col>
            <Col span={8} style={{ textAlign: "end" }}>
              <LanguageDropdown width="10px" height="10px" />
            </Col>
          </Row>
        </div>

        <Row gutter={24} justify="space-between">
          <Col span={12}>
            <Flex vertical gap="1rem" className={styles.profileSetupContainer}>
              <Title level={1} style={{ color: colors.primaryColor }}>
                {t("ProfileSetup.title")}
              </Title>
              <Title level={4} type="secondary" style={{ margin: 0 }}>
                {t("ProfileSetup.subTitle")}
              </Title>
              <Form
                form={form}
                layout="vertical"
                onFinish={onSubmit}
                autoComplete="off"
                requiredMark={false}
              >
                <Flex vertical gap="1rem">
                  <div>
                    <Form.Item
                      label={
                        <RequiredLabel
                          text={t("SignUp.nameLabel")}
                          required={true}
                        />
                      }
                      name="name"
                      rules={[
                        {
                          required: true,
                          message: t("SignUp.nameErrorMessage"),
                        },
                        {
                          whitespace: true,
                          message: t("SignUp.nameErrorMessage"),
                        },
                        {
                          validator: (_, value) => validatorFunctionToAllowOnlyEnglish(t("SignUp.nameErrorMessage"))(_,value),
                        }
                      ]}
                      normalize={(value) => sanitizeDataTypeClientSide(value)}
                    >
                      <Input 
                      placeholder={t("SignUp.namePlaceholder")} 
                      onKeyDown={(e) =>
                        allowAlphaNumericCharacters(e)
                      }
                      />
                    </Form.Item>
                    <Form.Item
                      label={
                        <RequiredLabel
                          text={t("SignUp.directorNameLabel")}
                          required={true}
                        />
                      }
                      name="director_name"
                      rules={[
                        {
                          required: true,
                          message: t("SignUp.directorNameErrorMessage"),
                        },
                        {
                          whitespace: true,
                          message: t("SignUp.directorNameErrorMessage"),
                        },
                        {
                          validator: (_, value) => validatorFunctionToAllowOnlyEnglish(t("SignUp.directorNameErrorMessage"))(_,value),
                        }
                      ]}
                      normalize={(value) => sanitizeDataTypeClientSide(value)}
                    >
                      <Input
                        placeholder={t("SignUp.directorNamePlaceholder")}
                        onKeyDown={(e) =>
                          allowAlphaNumericCharacters(e)
                        }
                      />
                    </Form.Item>

                    <Form.Item
                      label={
                        <RequiredLabel
                          text={t("ProfileSetup.branchLabel")}
                          required={true}
                        />
                      }
                      name="branch"
                      rules={[
                        {
                          required: true,
                          message: t("ProfileSetup.branchErrorMessage"),
                        },
                        {
                          whitespace: true,
                          message: t("ProfileSetup.branchErrorMessage"),
                        },
                      ]}
                      normalize={(value) => sanitizeDataTypeClientSide(value)}
                    >
                      <Input
                        placeholder={t("ProfileSetup.branchPlaceholder")}
                      />
                    </Form.Item>

                    <Form.Item
                      label={
                        <RequiredLabel
                          text={t("SignUp.designationLabel")}
                          required={true}
                        />
                      }
                      name="designation"
                      rules={[
                        {
                          required: true,
                          message: t("SignUp.designationErrorMessage"),
                        },
                        {
                          whitespace: true,
                          message: t("SignUp.designationErrorMessage"),
                        },
                      ]}
                      normalize={(value) => sanitizeDataTypeClientSide(value)}
                    >
                      <Input placeholder={t("SignUp.designationPlaceholder")} />
                    </Form.Item>

                    <Form.Item
                      label={
                        <RequiredLabel
                          text={t("ProfileSetup.addressLineOneLabel")}
                          required={true}
                        />
                      }
                      name="address_line_1"
                      rules={[
                        {
                          required: true,
                          message: t("ProfileSetup.addressLineOneErrorMessage"),
                        },
                        {
                          whitespace: true,
                          message: t("ProfileSetup.addressLineOneErrorMessage"),
                        },
                      ]}
                      normalize={(value) => sanitizeDataTypeClientSide(value)}
                    >
                      <Input
                        placeholder={t(
                          "ProfileSetup.addressLineOnePlaceholder"
                        )}
                      />
                    </Form.Item>

                    <Form.Item
                      label={
                        <RequiredLabel
                          text={t("ProfileSetup.addressLineTwoLabel")}
                          required={false}
                        />
                      }
                      name="address_line_2"
                      normalize={(value) => sanitizeDataTypeClientSide(value)}
                    >
                      <Input
                        placeholder={t(
                          "ProfileSetup.addressLineTwoPlaceholder"
                        )}
                      />
                    </Form.Item>

                    <Form.Item
                      label={
                        <RequiredLabel
                          text={t("ProfileSetup.countryLabel")}
                          required={true}
                        />
                      }
                      name="country"
                      rules={[
                        {
                          required: true,
                          message: t("ProfileSetup.countryErrorMessage"),
                        },
                      ]}
                    >
                      <Select
                        placeholder={t("ProfileSetup.countryPlaceholder")}
                        popupMatchSelectWidth={true}
                        onChange={(value, element) => {
                          handleCountryChange(value, element);
                        }}
                        showSearch
                        filterOption={(input, option) =>
                          (option?.children ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                      >
                        {countryList &&
                          countryList?.map((item) => (
                            <Select.Option key={item?._id} value={item?._id}>
                              {item?.name}
                            </Select.Option>
                          ))}
                      </Select>
                    </Form.Item>

                    <Form.Item
                      label={
                        <RequiredLabel
                          text={t("ProfileSetup.stateProviceLabel")}
                          required={true}
                        />
                      }
                      name="state"
                      rules={[
                        {
                          required: true,
                          message: t("ProfileSetup.stateProviceErrorMessage"),
                        },
                      ]}
                    >
                      <Select
                        placeholder={t("ProfileSetup.stateProvicePlaceholder")}
                        popupMatchSelectWidth={true}
                        onChange={(value, element) => {
                          handleStateChange(value, element);
                        }}
                        disabled={!isCountrySelected}
                        showSearch
                        filterOption={(input, option) =>
                          (option?.children ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                      >
                        {stateList?.map((item) => (
                          <Select.Option key={item?.id} value={item?.id}>
                            {item?.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Form.Item
                      label={
                        <RequiredLabel
                          text={t("ProfileSetup.cityLabel")}
                          required={true}
                        />
                      }
                      name="city"
                      rules={[
                        {
                          required: true,
                          message: t("ProfileSetup.cityErrorMessage"),
                        },
                      ]}
                    >
                      <Select
                        placeholder={t("ProfileSetup.cityPlaceholder")}
                        onChange={(value, element) => {
                          handleCityChange(value, element);
                        }}
                        popupMatchSelectWidth={true}
                        disabled={!isStateSelected}
                        showSearch
                        filterOption={(input, option) =>
                          (option?.children ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                      >
                        {cityList?.map((item) => (
                          <Select.Option key={item?.id} value={item?.id}>
                            {item?.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Form.Item
                      label={
                        <RequiredLabel
                          text={t("Modules.Services.ServicesForm.latitude")}
                          required={true}
                        />
                      }
                      name="latitude"
                      rules={[
                        {
                          required: true,
                          message: t(
                            "Modules.Services.ServicesForm.pleaseEnterLatitude"
                          ),
                        },
                        {
                          validator: (_, value) => {
                            if (value && value < 1)
                              return Promise.reject(
                                `${t(
                                  "Modules.Services.ServicesForm.latitude"
                                )} ${t("CommonUtils.shouldBeGreaterThan0")}`
                              );
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input
                        type="number"
                        placeholder={t(
                          "Modules.Services.ServicesForm.enterLatitude"
                        )}
                        onKeyDown={(e) =>
                          allowCharactersOnlyForDecimalNumbers(e)
                        }
                        onWheel={(e) => e.currentTarget.blur()}
                      />
                    </Form.Item>
                    <Form.Item
                      label={
                        <RequiredLabel
                          text={t("Modules.Services.ServicesForm.longitude")}
                          required={true}
                        />
                      }
                      name="longitude"
                      rules={[
                        {
                          required: true,
                          message: t(
                            "Modules.Services.ServicesForm.pleaseEnterLongitude"
                          ),
                        },
                        {
                          validator: (_, value) => {
                            if (value && value < 1)
                              return Promise.reject(
                                `${t(
                                  "Modules.Services.ServicesForm.longitude"
                                )} ${t("CommonUtils.shouldBeGreaterThan0")}`
                              );
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input
                        type="number"
                        placeholder={t(
                          "Modules.Services.ServicesForm.enterLongitude"
                        )}
                        onKeyDown={(e) =>
                          allowCharactersOnlyForDecimalNumbers(e)
                        }
                        onWheel={(e) => e.currentTarget.blur()}
                      />
                    </Form.Item>

                    <Form.Item
                      label={
                        <RequiredLabel
                          text={t("ProfileSetup.zipPostalLabel")}
                          required={true}
                        />
                      }
                      name="zip_code"
                      rules={[
                        {
                          required: true,
                          message: t("ProfileSetup.zipPostalErrorMessage"),
                        },
                        {
                          validator: (_, value) => {
                            if (value && value < 0)
                              return Promise.reject(
                                `${t("ProfileSetup.zipPostalLabel")} ${t(
                                  "CommonUtils.shouldBeGreaterThan0"
                                )}`
                              );
                            return Promise.resolve();
                          },
                        },
                      ]}
                      normalize={(value) => sanitizeDataTypeClientSide(value)}
                    >
                      <Input
                        type="number"
                        placeholder={t("ProfileSetup.zipPostalPlaceholder")}
                        onKeyDown={allowCharactersOnlyForDecimalNumbers}
                        onWheel={disableOnWheel}
                      />
                    </Form.Item>

                    <Form.Item
                      label={
                        <RequiredLabel
                          text={t("ProfileSetup.paymentTypeLabel")}
                          required={true}
                        />
                      }
                      name="payment_type"
                      rules={[
                        {
                          required: true,
                          message: t("ProfileSetup.paymentTypeErrorMessage"),
                        },
                      ]}
                    >
                      <Select
                        placeholder={t("ProfileSetup.paymentTypePlaceholder")}
                        suffixIcon={
                          <DropDownCloseIcon width={"10px"} height={"10px"} />
                        }
                        popupMatchSelectWidth={true}
                      >
                        {paymentData?.map((optionValue) => (
                          <Select.Option
                            key={optionValue?.value}
                            value={optionValue?.value}
                          >
                            {optionValue?.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </div>

                  <Flex vertical gap="1rem" align="center" justify="center">
                    <Button
                      type="primary"
                      htmlType="submit"
                      style={{ width: "100%" }}
                      loading={loading}
                    >
                      {t("ProfileSetup.complete")}
                    </Button>
                  </Flex>
                </Flex>
              </Form>
            </Flex>
          </Col>
          <Col span={12}>
          <Flex vertical gap="2rem" align="center">
            <Flex vertical align="center" style={{ position: "relative" }}>
              <FileAttachment
                attachmentObj={singleAttachmentObj}
                attachmentUrl={singleAttachmentSelectedImageUrl}
                loading={imageLoader}
              />

              <div
                className="attachmentContainer"
                style={{
                  cursor: "pointer",
                  position: "absolute",
                  bottom: "-25px",
                  bottom: "-25px",
                  background:colors.turquoiseGreen,
                  borderRadius:10,
                }}
                onClick={() => attachmentRef.current?.click()}
              >
                <Button
                  type="primary"
                  shape="circle"
                  size="middle"
                  style={{ background:colors.turquoiseGreen}}
                  icon={<CameraOutlined style={{ fontSize: 22 }} />}
                />
              </div>
              <input
                type="file"
                style={{ display: "none" }}
                ref={attachmentRef}
                accept="image/*"
                onChange={onImageChanged}
              />
            </Flex>
            <Typography.Title level={3} type="secondary" style={{ marginBottom: 10 }}>
                {t("CommonUtils.supportedImageFormats")}
              </Typography.Title>
              </Flex>
          </Col>
        </Row>
      </Flex>
    </>
  );
};

export default ProfileSetup;
